import { useCallback, useState } from "react";
import AppShell from "./components/AppShell";
import StampCard from "./components/StampCard";
import QrScannerModal from "./components/QrScannerModal";
import { useAuth } from "@/context/AuthContext";
import { formatMemberIdDisplay } from "@/lib/formatMemberId";
import BrandLogo from "@/components/BrandLogo";
import { openHttpsUrl } from "@/lib/openExternalUrl";
import ShopContactSection from "@/components/ShopContactSection";
import VisitSummaryCard from "./components/VisitSummaryCard";
import {
  memberRankLabelEn,
  parseMemberRankKey,
  rankBadgePillStyle,
} from "@/lib/memberRank";

const HOTPEPPER_WEB_URL = "https://beauty.hotpepper.jp/kr/slnH000665299/";

export default function HomePage() {
  const { user, loading, firebaseUser, getIdToken, refreshUser } = useAuth();
  const [qrActive, setQrActive] = useState(false);

  const onRedeemed = useCallback(() => {
    void refreshUser();
  }, [refreshUser]);

  const openHotpepper = useCallback(() => {
    void openHttpsUrl(HOTPEPPER_WEB_URL);
  }, []);

  if (loading && !user) {
    return (
      <AppShell>
        <div className="px-4 py-16 text-center flex items-center justify-center">
          <BrandLogo compact />
        </div>
      </AppShell>
    );
  }

  if (!user || !firebaseUser) {
    return (
      <AppShell>
        <div className="px-4 py-16 text-center space-y-2">
          <p className="text-sm" style={{ color: "#ff8080" }}>
            会員情報を表示できません。再度ログインしてください。
          </p>
        </div>
      </AppShell>
    );
  }

  const rankKey = parseMemberRankKey(user.rank);
  const rankPill = rankBadgePillStyle(rankKey);

  return (
    <AppShell>
      <div className="px-4 py-5 space-y-5 max-w-lg mx-auto">
        <div
          className="rounded-2xl px-5 py-4 flex items-center justify-between"
          style={{
            background: "linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(184,134,11,0.08) 100%)",
            border: "1px solid rgba(212,175,55,0.25)",
          }}
        >
          <div>
            <p className="text-xs mb-1" style={{ color: "rgba(244,229,195,0.6)" }}>おかえりなさい</p>
            <p className="text-base font-bold" style={{ color: "#f4e5c3" }}>{user.name} 様</p>
            <p className="text-[10px] mt-1 font-mono" style={{ color: "rgba(244,229,195,0.35)" }}>
              {formatMemberIdDisplay(user.memberId)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs mb-1" style={{ color: "rgba(244,229,195,0.6)" }}>会員ランク</p>
            <span
              className="text-xs font-bold px-3 py-1 rounded-full"
              style={{
                background: rankPill.background,
                color: rankPill.color,
                border: rankPill.border,
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              {memberRankLabelEn(rankKey)}
            </span>
          </div>
        </div>

        <VisitSummaryCard user={user} />

        <div className="flex items-center gap-3">
          <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.2)" }} />
          <span className="text-xs tracking-widest" style={{ color: "rgba(212,175,55,0.5)", fontFamily: "Montserrat, sans-serif" }}>
            STAMPS
          </span>
          <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.2)" }} />
        </div>

        <div className="space-y-3">
          <StampCard user={user} />
          <button
            type="button"
            onClick={() => setQrActive(true)}
            className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl cursor-pointer transition-all duration-200 active:scale-95"
            style={{
              background: "linear-gradient(135deg, #b8860b 0%, #d4af37 50%, #b8860b 100%)",
            }}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-qr-scan-2-line text-xl" style={{ color: "#1a1a2e" }}></i>
            </div>
            <span className="text-sm font-bold tracking-wider whitespace-nowrap" style={{ color: "#1a1a2e", fontFamily: "Montserrat, sans-serif" }}>
              QRコードを読み取る
            </span>
          </button>
        </div>

        <ShopContactSection layout="row" onReserveClick={openHotpepper} reserveLabel="予約" />
      </div>

      <QrScannerModal
        open={qrActive}
        getIdToken={getIdToken}
        onClose={() => setQrActive(false)}
        onRedeemed={onRedeemed}
      />
    </AppShell>
  );
}
